import { Alert, Card, IconButton, Typography } from "@mui/material";
import { ReactElement } from "react";
import { RecordContextProvider, useShowController } from "react-admin";
import { currentUser, grants, restrictAccessToDrivingSchoolIds } from "../../backoffice.access_control";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { AutovioMap } from "../../misc/maps";
import { PageTitle } from "../../misc/PageTitle";
import { LatestDrivingStudentsCard } from "./cards/LatestDrivingStudentsCard";
import { MapCard } from "./cards/MapCard";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { openInFirestoreConsole } from "../../firebase";
import FirestoreIcon from "@mui/icons-material/LocalFireDepartment";
import { NotesOrThreadWrapper } from "./NotesOrThreadWrapper";
import { StudentsSignupsStatsCard } from "./cards/StudentsSignupsStatsCard";
import Box from "@mui/material/Box";
import { StatisticsOverviewCard } from "./cards/StatisticsOverviewCard";
import { DateTime } from "luxon";
import { RightCollapsibleSidebar } from "../../misc/RightCollapsibleSidebar";
import { CurrentExamsCard } from "./cards/CurrentExamsCard";

export function DrivingSchoolDashboard(): ReactElement | null {
  const [{ drivingSchoolId }] = useAutovioContext();
  const greeting = currentUser?.callName ? `Hallo, ${currentUser.callName} 👋🏻` : "Hallo 👋🏻";
  const { record: drivingSchool } = useShowController({
    resource: "drivingSchools",
    id: drivingSchoolId,
    queryOptions: { enabled: !!drivingSchoolId },
  });

  if (!drivingSchoolId) {
    if (restrictAccessToDrivingSchoolIds) {
      return (
        <Box className="centered">
          <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
        </Box>
      );
    }

    return (
      <div data-testid="DrivingSchoolDashboard" style={{ height: "100%" }}>
        <PageTitle>AUTOVIO Fahrschulen</PageTitle>
        <Card sx={{ mt: "10px", height: "calc(100% - 66px)" }}>
          <AutovioMap />
        </Card>
      </div>
    );
  }

  if (!drivingSchool) {
    return <LoadingIndicator />;
  }

  return (
    <RecordContextProvider value={drivingSchool}>
      <Column data-testid="DrivingSchoolDashboard" sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Row gap={2.25} sx={{ marginBottom: "30px", marginTop: "10px", alignItems: "center" }}>
          <PageTitle documentTitle={drivingSchool.name}>
            <Column gap={0}>
              <Row alignItems="center">
                <Typography variant="h2" sx={{ fontSize: "1.75em", fontWeight: 700, lineHeight: 1.08 }}>
                  {greeting}
                </Typography>
              </Row>
              <Row alignItems="center" sx={{ marginTop: "4px" }}>
                <Typography variant="body2" sx={{ fontWeight: "normal", color: "text.secondary" }}>
                  {drivingSchool.name}
                  {drivingSchool.postalAddress?.city ? ` · ${drivingSchool.postalAddress.city}` : ""}
                </Typography>
                {grants.has("viewFirestore") && (
                  <div>
                    <IconButton
                      title="in Firestore öffnen"
                      size="small"
                      onClick={() => openInFirestoreConsole(`/driving_schools/${drivingSchoolId}`)}
                    >
                      <FirestoreIcon />
                    </IconButton>
                  </div>
                )}
              </Row>
            </Column>
          </PageTitle>
        </Row>
        {!drivingSchool.isActive && (
          <Row sx={{ marginBottom: "30px" }}>
            <Alert severity="warning" sx={{ width: "100%" }}>
              {drivingSchool.startDate > DateTime.now()
                ? `Fahrschule inaktiv, wird am ${drivingSchool.startDate.toLocaleString(DateTime.DATE_SHORT, { locale: "de-DE" })} automatisch aktiv.`
                : `Fahrschule inaktiv, wurde am ${drivingSchool.endDate!.toLocaleString(DateTime.DATE_SHORT, { locale: "de-DE" })} deaktiviert.`}
            </Alert>
          </Row>
        )}

        <Column gap={{ xs: 3, md: 3 }}>
          <StatisticsOverviewCard />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gridTemplateAreas: {
                xs: `"students" "reports" "exams" "map"`,
                lg: `"students reports" "exams map"`,
                xl: `"students reports exams map"`,
              },
              gap: { xs: 2, lg: "30px" },
              minWidth: 0,
              maxWidth: "100%",
              alignItems: { xs: "start", lg: "stretch" },
            }}
          >
            <Box sx={{ gridArea: "students", minWidth: 0 }}>
              <LatestDrivingStudentsCard />
            </Box>
            <Box sx={{ gridArea: "reports", minWidth: 0 }}>
              <StudentsSignupsStatsCard />
            </Box>
            <Box sx={{ gridArea: "exams", minWidth: 0 }}>
              <CurrentExamsCard />
            </Box>
            <Box sx={{ gridArea: "map", minWidth: 0 }}>
              <MapCard variant="matchGridRow" />
            </Box>
          </Box>
        </Column>
        <RightCollapsibleSidebar title={drivingSchool.name} variant="overlay">
          <NotesOrThreadWrapper />
        </RightCollapsibleSidebar>
      </Column>
    </RecordContextProvider>
  );
}
